import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  type ItemSyncStatusResponse,
  ItemSyncStatusView,
} from '@/common/api/types/workIntegrations';
import { getItemSyncStatuses } from '@/features/settings/work-integrations/api/itemSyncStatus';
import { workIntegrationsQueryKeys } from '@/features/settings/work-integrations/queryKeys';

const POLL_INTERVAL_MS = 3_000;

export const hasInFlightState = (
  data: Record<number, ItemSyncStatusResponse> | undefined,
): boolean => {
  if (!data) return false;
  for (const entry of Object.values(data)) {
    if (
      entry.state === ItemSyncStatusView.PENDING ||
      entry.state === ItemSyncStatusView.IN_PROGRESS
    ) {
      return true;
    }
  }
  return false;
};

export const useItemSyncStatusesQuery = (
  itemIds: number[],
  queryOptions: Omit<
    UseQueryOptions<Record<number, ItemSyncStatusResponse>, Error>,
    'queryFn' | 'queryKey'
  > = {},
) => {
  const itemIdSet = useMemo(() => new Set(itemIds), [itemIds]);

  return useQuery<Record<number, ItemSyncStatusResponse>, Error>({
    queryKey: workIntegrationsQueryKeys.itemSyncStatuses({ itemIds: itemIdSet }),
    queryFn: () => getItemSyncStatuses({ itemIds: itemIdSet }),
    enabled: itemIdSet.size > 0,
    refetchInterval: (query) => (hasInFlightState(query.state.data) ? POLL_INTERVAL_MS : false),
    ...queryOptions,
  });
};
